import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  feePromotionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { feeEngineEndpoints } from "@/features/fee-engine/api/endpoints";
import type { PromotionDto } from "@/features/fee-engine/api/dto";

/**
 * Active une promotion (`POST /Promotions/{id}/activate`, guide §6.5) —
 * son code devient utilisable dans un devis de frais.
 */
export const useActivatePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["fee-engine", "promotions", "activate"],
    mutationFn: (promotionId: string) =>
      mutateData({
        url: feeEngineEndpoints.promotions.activate(promotionId),
        method: "POST",
      }) as Promise<PromotionDto>,
    onSuccess: (_data, promotionId) => {
      return invalidateAfterMutation(
        queryClient,
        feePromotionCacheKeys(promotionId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useActivatePromotion failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "fee-engine" }
      );
    },
  });
};
